import React from 'react';
import { Descriptions, Tag, Tooltip, Badge, Row, Col, Divider, Typography, Card, Grid, Avatar } from "antd";
import { WhatsAppOutlined, FacebookOutlined, MessageOutlined, InstagramOutlined, CalendarOutlined, SyncOutlined, StopOutlined, UserOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { RTL_LANGUAGES } from '../../../Constants/language';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const getPlatformIcon = (channelType) => {
    switch (channelType?.toLowerCase()) {
        case 'whatsapp':
            return <WhatsAppOutlined />;
        case 'messenger':
            return <MessageOutlined />;
        case 'facebook_page':
            return <FacebookOutlined />;
        case 'instagram':
            return <InstagramOutlined />;
        default:
            return <MessageOutlined />;
    }
};

const getPlatformColor = (channelType) => {
    switch (channelType?.toLowerCase()) {
        case 'whatsapp':
            return '#25D366';
        case 'messenger':
        case 'facebook_page':
            return '#1877F2';
        case 'instagram':
            return '#E4405F';
        default:
            return '#888';
    }
};

const getTokenExpiryColor = (expiresAt, isPermanent) => {
    if (isPermanent) return 'success';
    if (!expiresAt) return 'default';

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = (expiryDate - now) / (1000 * 60 * 60 * 24);

    if (daysUntilExpiry < 0) return 'error';
    if (daysUntilExpiry < 7) return 'warning';
    return 'processing';
};

const getTokenExpiryText = (expiresAt, isPermanent, t) => {
    if (isPermanent) return t('permanent_token');
    if (!expiresAt) return t('unknown');

    const expiryDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiryDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return t('expired');
    if (daysUntilExpiry === 0) return t('expires_today');
    if (daysUntilExpiry === 1) return t('expires_tomorrow');
    if (daysUntilExpiry < 7) return t('expires_in_days', { days: daysUntilExpiry });
    return t('valid');
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
};

const ChannelDetailsModal = ({ channel, onCancel }) => {
    const { t, i18n } = useTranslation();
    const screens = useBreakpoint();
    const isRtl = RTL_LANGUAGES.includes(i18n.language.split('-')[0]);

    if (!channel) return null;

    const tokenColor = getTokenExpiryColor(channel.token_expires_at, channel.is_permanent_token);
    const tokenText = getTokenExpiryText(channel.token_expires_at, channel.is_permanent_token, t);
    const platformColor = getPlatformColor(channel.channel_type);

    return (
        <ResponsiveModal
            title={t('channel_details')}
            open
            onCancel={onCancel}
            desktopWidth={600}
            footer={null}
        >
            {/* Header Section */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
                <Badge
                    count={
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: platformColor,
                            border: '2px solid #fff'
                        }}>
                            {React.cloneElement(getPlatformIcon(channel.channel_type), {
                                style: { fontSize: 16, color: '#fff' }
                            })}
                        </div>
                    }
                    offset={[-10, 70]}
                >
                    <Avatar
                        size={84}
                        shape="square"
                        src={channel.picture_url}
                        icon={React.cloneElement(getPlatformIcon(channel.channel_type), {
                            style: { fontSize: 42 }
                        })}
                        style={{
                            backgroundColor: channel.picture_url ? 'transparent' : platformColor,
                            borderRadius: '12px',
                            border: channel.picture_url ? 'none' : `3px solid ${platformColor}`
                        }}
                    />
                </Badge>
                <Title level={4} style={{ marginTop: 16, marginBottom: 0 }}>
                    {channel.name || channel.external_channel_id}
                </Title>
                {channel.channel_json?.category && (
                    <Text type="secondary" style={{ fontSize: '13px', marginTop: 4 }}>
                        {channel.channel_json.category}
                    </Text>
                )}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Tooltip title={t('token_status')}>
                        <Tag color={tokenColor} icon={<SafetyCertificateOutlined />}>
                            {tokenText}
                        </Tag>
                    </Tooltip>
                    <Tag color={channel.is_synchronization_enabled ? 'processing' : 'default'}>
                        {channel.is_synchronization_enabled ? (
                            <><SyncOutlined spin style={{ marginRight: 4 }} />{t('synced')}</>
                        ) : (
                            <><StopOutlined style={{ marginRight: 4 }} />{t('not_synced')}</>
                        )}
                    </Tag>
                </div>
            </div>

            <Card bordered={false} style={{ background: '#fafafa' }}>
                {/* Channel Information */}
                <Divider orientation={isRtl ? "right" : "left"} style={{ marginTop: 0 }}>{t('channel_info')}</Divider>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Descriptions column={1} size="small" colon={false} layout={screens.xs ? 'vertical' : 'horizontal'}>
                            <Descriptions.Item label={t('channel_type')}>
                                <Tag color={platformColor}>{t(channel.channel_type || 'unknown')}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('external_channel_id')}>
                                <Text code copyable style={{ wordBreak: 'break-all' }}>{channel.external_channel_id}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('id')}>
                                <Text code style={{ wordBreak: 'break-all' }}>{channel.id}</Text>
                            </Descriptions.Item>
                            {channel.agent && (
                                <Descriptions.Item label={t('agent')}>
                                    <Badge
                                        status={channel.agent.is_started ? "success" : "default"}
                                        text={
                                            <span>
                                                <UserOutlined style={{ marginRight: 8 }} />
                                                {channel.agent.name}
                                                {channel.agent.is_started ? ` (${t('started')})` : ` (${t('stopped')})`}
                                            </span>
                                        }
                                    />
                                </Descriptions.Item>
                            )}
                            {channel.credential && (
                                <Descriptions.Item label={t('credential')}>
                                    <Text>
                                        {channel.credential.name || channel.credential.email || channel.credential.credential_user_id}
                                        {channel.credential.has_error && (
                                            <Tag color="error" style={{ marginLeft: 8 }}>{t('error')}</Tag>
                                        )}
                                    </Text>
                                </Descriptions.Item>
                            )}
                            {channel.description && (
                                <Descriptions.Item label={t('description')}>
                                    <Text>{channel.description}</Text>
                                </Descriptions.Item>
                            )}
                            {channel.channel_json?.about && (
                                <Descriptions.Item label={t('about')}>
                                    <Text type="secondary">{channel.channel_json.about}</Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Col>
                </Row>

                {/* Token Information */}
                <Divider orientation={isRtl ? "right" : "left"}>{t('authentication')}</Divider>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Descriptions column={1} size="small" colon={false} layout={screens.xs ? 'vertical' : 'horizontal'}>
                            <Descriptions.Item label={t('is_permanent_token')}>
                                <Tag color={channel.is_permanent_token ? 'success' : 'default'}>
                                    {channel.is_permanent_token ? t('yes') : t('no')}
                                </Tag>
                            </Descriptions.Item>
                            {channel.token_expires_at && (
                                <Descriptions.Item label={<SpaceLabel icon={<CalendarOutlined />} text={t('token_expires_at')} />}>
                                    {formatDate(channel.token_expires_at)}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Col>
                </Row>

                <Divider orientation={isRtl ? "right" : "left"} style={{ fontSize: '12px', color: '#888', fontWeight: 'normal' }}>{t('metadata')}</Divider>
                <Row gutter={[0, 4]}>
                    <Col xs={24} sm={12}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {t('created_at')}: {formatDate(channel.created_at)}
                        </Text>
                    </Col>
                    {channel.updated_at && (
                        <Col xs={24} sm={12} style={{ textAlign: screens.xs ? 'left' : 'right' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {t('updated_at')}: {formatDate(channel.updated_at)}
                            </Text>
                        </Col>
                    )}
                </Row>
            </Card>
        </ResponsiveModal>
    );
};

const SpaceLabel = ({ icon, text }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {icon}
        {text}
    </span>
);

export default ChannelDetailsModal;
